import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { MOCK_CLIENTS_FULL } from '../data/mockData';
import ClientProfile from '../components/ClientProfile';
import { supabase } from '../supabaseClient';

// 🚨 THE UPGRADE: Define production stages directly to decouple from mock data!
const LIVE_JOURNEY_STAGES = [
  { id: 'registered', label: 'Registered', color: 'bg-slate-500' },
  { id: 'kyc_pending', label: 'KYC Pending', color: 'bg-amber-500' },
  { id: 'kyc_rejected', label: 'KYC Rejected', color: 'bg-red-500' },
  { id: 'kyc_approved', label: 'KYC Approved', color: 'bg-blue-500' },
  { id: 'edd_required', label: '⚠️ EDD Required', color: 'bg-rose-500' },
  { id: 'first_deposit', label: 'First Deposit', color: 'bg-purple-500' },
  { id: 'active', label: 'Active Trader', color: 'bg-green-500' }
];

const ClientsPage = ({ userRole, currentUser }) => {
  // --- MASTER STATE ---
  const [allMappedClients, setAllMappedClients] = useState([]);
  const [filterStage, setFilterStage] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null); 

  // --- SEARCH & PAGINATION STATE ---
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // 🚨 NEW: Debounce search to prevent spamming the database on every keystroke
  useEffect(() => {
      const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
      return () => clearTimeout(timer);
  }, [searchQuery]);

  // 🚨 NEW: Reset to page 1 when changing tabs or searching
  useEffect(() => {
      setCurrentPage(1);
  }, [debouncedSearch, filterStage]);

  // Load LIVE data from Supabase
  useEffect(() => {
    const loadClients = async () => {
      setIsLoading(true);
      try {
        // 1. Build the Broker Query
        let brokerQuery = supabase
            .from('user_broker_accounts')
            .select('*')
            .order('created_at', { ascending: false });

        // 🚨 RESTORED: Add the search filter if the user is typing
        if (debouncedSearch) {
            const searchStr = debouncedSearch.toLowerCase();
            brokerQuery = brokerQuery.or(`name.ilike.%${searchStr}%,email.ilike.%${searchStr}%,account_id.ilike.%${searchStr}%`);
        } else {
            // Only limit to 1000 if we aren't searching
            brokerQuery = brokerQuery.limit(1000);
        }

        const { data: brokerAccounts, error: brokerErr } = await brokerQuery;

        // 2. Build the Profiles Query
        let profileQuery = supabase
            .from('profiles')
            .select('user_id, email, full_name, membership_tier, kyc_status, lifetime_volume, edd_required, advanced_kyc_status');

        // 🚨 RESTORED: Add the search filter for profiles too
        if (debouncedSearch) {
            const searchStr = debouncedSearch.toLowerCase();
            profileQuery = profileQuery.or(`full_name.ilike.%${searchStr}%,email.ilike.%${searchStr}%`);
        } else {
            profileQuery = profileQuery.limit(1000);
        }

        const { data: profileAccounts, error: profileErr } = await profileQuery;

        if (brokerErr || profileErr) throw brokerErr || profileErr;

        const unifiedAccounts = {};

        // 1. Process broker accounts first (they are our "primary" source of truth)
        (brokerAccounts || []).forEach(acc => {
            const email = (acc.email || acc.user_id || '').toLowerCase().trim();
            if (!email) return;
            unifiedAccounts[email] = { ...acc, source: 'broker' };
        });

        // 2. Merge profiles (enriching the existing records)
        (profileAccounts || []).forEach(prof => {
            const email = (prof.email || '').toLowerCase().trim();
            if (!email) return;
            
            if (unifiedAccounts[email]) {
                // Enrich existing
                const existing = unifiedAccounts[email];
                if (!existing.name && prof.full_name) existing.name = prof.full_name;
                if (!existing.kyc_status && prof.kyc_status) existing.kyc_status = prof.kyc_status;
                existing.membership_tier = prof.membership_tier || existing.membership_tier;
                existing.lifetime_volume = prof.lifetime_volume || 0;
                existing.edd_required = prof.edd_required || false;
                existing.advanced_kyc_status = prof.advanced_kyc_status;
            } else {
                // Create new from profile if missing from broker
                unifiedAccounts[email] = { 
                    ...prof, 
                    name: prof.full_name,
                    id: prof.user_id, 
                    is_missing_broker_row: true 
                };
            }
        });

        // 3. Process KYC Documents
        const emails = Object.keys(unifiedAccounts).filter(Boolean);
        let allCrmDocs = [];
        
        if (emails.length > 0) {
            // 🚨 THE FIX: Chunk the requests to prevent 400 Bad Request URL limits!
            const chunkSize = 150; 
            for (let i = 0; i < emails.length; i += chunkSize) {
                const chunk = emails.slice(i, i + chunkSize);
                const { data: crmDocs, error: docError } = await supabase
                    .from('crm_documents')
                    .select('client_email, status')
                    .in('client_email', chunk);
                    
                if (crmDocs) {
                    allCrmDocs = allCrmDocs.concat(crmDocs);
                } else if (docError) {
                    console.error("Failed to fetch document chunk:", docError);
                }
            }
        }

        // 4. Map the Final UI State
        const finalClients = Object.values(unifiedAccounts).map(acc => {
            const safeEmail = (acc.email || acc.user_id || '').toLowerCase().trim();
            
            // 1. Determine Approval Status
            // Check both: the boolean column OR the string status
            const isApproved = acc.kyc_approved === true || String(acc.kyc_status).toLowerCase() === 'approved';

            // 2. Determine Name (Highest Priority: The database 'name' column)
            let displayName = acc.name || acc.full_name || `${acc.first_name || ''} ${acc.last_name || ''}`.trim() || safeEmail.split('@')[0];
            if (!displayName || displayName === 'undefined') displayName = 'Unknown Client';

            // 3. Determine Journey Status
            let liveStatus = 'registered';
            const isEddRequired = Boolean(acc.edd_required) || (Number(acc.lifetime_volume || 0) >= 2000 && acc.advanced_kyc_status !== 'Completed');
            
            if (isApproved) {
                liveStatus = isEddRequired ? 'edd_required' : 'kyc_approved';
            } else {
                // Only check documents if NOT approved
                const userDocs = allCrmDocs.filter(d => (d.client_email || '').toLowerCase() === safeEmail);
                const hasPendingDocs = userDocs.some(d => ['pending', 'pending review', 'awaiting review'].includes(String(d.status).toLowerCase()));
                const hasRejectedDocs = userDocs.some(d => String(d.status).toLowerCase() === 'rejected');

                if (hasRejectedDocs || ['declined', 'rejected'].includes(String(acc.kyc_status).toLowerCase())) {
                    liveStatus = 'kyc_rejected';
                } else if (hasPendingDocs || ['pending', 'in_review', 'in_progress', 'not started'].includes(String(acc.kyc_status).toLowerCase())) {
                    liveStatus = 'kyc_pending';
                }
            }

            return {
                id: acc.user_id || acc.id || acc.account_id,
                name: displayName,
                avatar: displayName.charAt(0).toUpperCase(),
                status: liveStatus,
                edd_required: isEddRequired,
                lifetime_volume: Number(acc.lifetime_volume || 0),
                advanced_kyc_status: acc.advanced_kyc_status,
                tier: acc.membership_tier || 'Standard',
                risk: isEddRequired ? 'High' : 'Low',
                email: safeEmail.includes('@') ? safeEmail : 'N/A',
                phone: acc.phone || 'N/A',
                is_missing_broker_row: acc.is_missing_broker_row
            };
        });

        setAllMappedClients(finalClients);
      } catch (error) {
        console.error("Data merge error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadClients();
  }, [debouncedSearch]);

  // 4. INSTANT CLIENT-SIDE FILTERING & PAGINATION
  const filteredClients = allMappedClients.filter(c => {
      if (filterStage === 'all') return true;
      if (filterStage === 'edd_required') return c.status === 'edd_required' || c.edd_required === true;
      return c.status === filterStage;
  });
  const totalPages = Math.ceil(filteredClients.length / 50) || 1;
  const paginatedClients = filteredClients.slice((currentPage - 1) * 50, currentPage * 50);
  
  // Permission logic
  const showFinancials = ['Manager', 'Sales', 'Compliance', 'Admin'].includes(userRole);

// --- UI HELPER: DYNAMIC STATUS COLORS ---
  const getStatusStyles = (statusId) => {
      const id = statusId?.toLowerCase();
      if (id === 'registered') return 'bg-slate-500/10 text-slate-300 border-slate-500/20';
      if (id === 'kyc_pending') return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      if (id === 'kyc_rejected') return 'bg-red-500/10 text-red-500 border-red-500/30';
      if (id === 'kyc_approved') return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      if (id === 'edd_required') return 'bg-rose-500/10 text-rose-400 border-rose-500/30';
      if (id === 'first_deposit') return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      if (id === 'active') return 'bg-green-500/10 text-green-400 border-green-500/20';
      if (id === 'dormant') return 'bg-slate-500/10 text-slate-400 border-slate-500/20';
      if (id === 'frozen') return 'bg-red-500/10 text-red-400 border-red-500/20';
      return 'bg-slate-700/50 text-slate-400 border-slate-600';
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
         <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Client Management</h2>
            <p className="text-slate-400 mt-1">Lifecycle Stage & Journey Management</p>
         </div>
         <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            {/* --- NEW: SEARCH INPUT --- */}
            <div className="relative w-full sm:w-64">
               <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
               <input 
                  type="text" 
                  placeholder="Search name, email, or ID..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-amber-500 focus:border-amber-500 outline-none transition-all"
               />
            </div>
            <button className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-slate-900 px-4 py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors whitespace-nowrap">
               <Plus size={16} /> Add Client
            </button>
         </div>
      </div>

      <div className="mb-8 overflow-x-auto no-scrollbar">
         <div className="flex space-x-3 pb-2 min-w-max">
            <button onClick={() => setFilterStage('all')} className={`px-4 py-2 rounded-lg border text-sm font-bold transition-all ${filterStage === 'all' ? 'bg-white text-slate-900 border-white' : 'bg-slate-900 text-slate-400 border-slate-700'}`}>All Clients</button>
            {LIVE_JOURNEY_STAGES.map(stage => {
               // Update badge counts dynamically based on current state
               const count = allMappedClients.filter(c => c.status === stage.id).length;
               return (
                  <button key={stage.id} onClick={() => setFilterStage(stage.id)} className={`px-4 py-2 rounded-lg border text-sm font-bold transition-all flex items-center gap-2 ${filterStage === stage.id ? 'bg-slate-800 border-amber-500 text-amber-500' : 'bg-slate-900 text-slate-400 border-slate-700'}`}>
                     <div className={`w-2 h-2 rounded-full ${stage.color}`}></div>{stage.label}<span className="bg-slate-800 px-1.5 rounded text-xs ml-1 opacity-70">{count}</span>
                  </button>
               )
            })}
         </div>
      </div>

      <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden shadow-xl overflow-x-auto relative">
         <table className="w-full text-left min-w-[1200px]">
            <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase border-b border-slate-800">
               <tr>
                  <th className="px-6 py-4">Client</th>
                  <th className="px-6 py-4">Journey Stage</th>
                  <th className="px-6 py-4">Tier</th>
                  <th className="px-6 py-4">Risk</th>
                  
                  {/* Conditional Headers */}
                  {showFinancials && (
                    <>
                      <th className="px-6 py-4 text-right">Trading Bal</th>
                      <th className="px-6 py-4 text-right">Bank Bal</th>
                      <th className="px-6 py-4 text-right">Crypto Bal</th>
                    </>
                  )}
                  
                  <th className="px-6 py-4">Agent</th>
                  <th className="px-6 py-4">Action</th>
               </tr>
            </thead>
<tbody className="divide-y divide-slate-800">
    {paginatedClients.map(client => (
        <tr key={client.id} className="hover:bg-slate-800/50 transition-colors cursor-pointer group" onClick={() => setSelectedClient(client)}>
                     <td className="px-6 py-4 font-medium text-white flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold border border-slate-700">
                            {client.avatar || 'U'}
                        </div>
                        <div>
                            <div className="group-hover:text-blue-400 transition-colors">{client.name}</div>
                            <div className="text-xs text-slate-500 font-mono">{client.id}</div>
                        </div>
                     </td>
                     
                     {/* FIX 1: DYNAMIC COLOR CODED JOURNEY TAGS */}
                     <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                           <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${getStatusStyles(client.status)}`}>
                              {LIVE_JOURNEY_STAGES.find(s => s.id === client.status)?.label || client.status}
                           </span>
                           {client.edd_required && client.status !== 'edd_required' && (
                              <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40 animate-pulse">
                                 EDD Req
                              </span>
                           )}
                        </div>
                     </td>
                     
                     <td className="px-6 py-4 text-slate-300 text-sm">{client.tier || 'Standard'}</td>
                     
                     {/* FIX 2: NORMALIZED RISK DOTS & FONT CASING */}
                     <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${client.risk?.toLowerCase() === 'low' || !client.risk ? 'bg-green-500' : client.risk?.toLowerCase() === 'medium' ? 'bg-amber-500' : 'bg-red-500'}`}></div>
                            <span className="text-slate-300 text-sm capitalize">{client.risk?.toLowerCase() || 'low'}</span>
                        </div>
                     </td>
                     
                     {/* Conditional Data Cells */}
                     {showFinancials && (
                       <>
                         <td className="px-6 py-4 text-slate-300 font-mono text-right">
                           ${client.trading?.equity?.toLocaleString() || client.equity?.toLocaleString() || '0'}
                         </td>
                         <td className="px-6 py-4 text-slate-300 font-mono text-right">
                           {client.banking?.currency === 'GBP' ? '£' : '€'}
                           {client.banking?.balance?.toLocaleString() || '0'}
                         </td>
                         <td className="px-6 py-4 text-slate-300 font-mono text-right">
                           {client.crypto?.balance ? `€${client.crypto.balance.toLocaleString()}` : '-'}
                         </td>
                       </>
                     )}
                     
                     <td className="px-6 py-4 text-slate-400 text-sm">{client.agent || 'Unassigned'}</td>
                     <td className="px-6 py-4 text-blue-400 font-bold text-xs hover:underline">View Profile</td>
                  </tr>
               ))}
               {filteredClients.length === 0 && (
                   <tr>
                       <td colSpan="10" className="p-12 text-center text-slate-500 italic">No clients found.</td>
                   </tr>
               )}
</tbody>
         </table>
      </div>

      {/* --- PAGINATION CONTROLS --- */}
      <div className="flex items-center justify-between mt-6 bg-slate-900 p-4 rounded-xl border border-slate-800">
          <p className="text-sm text-slate-400">
              {isLoading ? 'Loading data...' : `Showing Page ${currentPage} of ${totalPages}`}
          </p>
          <div className="flex gap-2">
              <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1 || isLoading}
                  className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-bold disabled:opacity-50 hover:bg-slate-700 transition-colors"
              >
                  Previous
              </button>
              <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || isLoading}
                  className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-bold disabled:opacity-50 hover:bg-slate-700 transition-colors"
              >
                  Next
              </button>
          </div>
      </div>

      {/* --- CLIENT PROFILE MODAL --- */}
      {selectedClient && (
        <ClientProfile 
          client={selectedClient} 
          onClose={() => setSelectedClient(null)} 
          onClientSwitch={(newClient) => setSelectedClient(newClient)}
          userRole={userRole} 
          currentUser={currentUser}
        />
      )}

    </div>
  );
};

export default ClientsPage;